import * as LodashFP from 'lodash/fp'

import N from '../../utils/assert-never'
import {
  FormFieldBlueprint,
  FormBlueprint,
  OptionMap,
  FieldValidation,
  IFormValidation,
  isFormBlueprint,
  FormFieldList,
  isFormOrNestedFormBlueprint,
  FieldGuidelinesType,
  FormGuidelinesType,
  emptyFieldGuidelines,
  GuidelineCondition,
  Guidelines,
} from '../../interfaces/components/dynamic-form'
import { IStringAnyMap, IStringTMap } from '../../types/map'
import {
  KeyPath,
  stringPath,
  arrayPathFlobulator,
  filterKeys,
} from '../../utils/object'

import { removeFirstElementFromPathString } from '../../utils/string'

function initializeField(fieldBlueprint: FormFieldBlueprint) {
  switch (fieldBlueprint.type) {
    case 'text':
      return fieldBlueprint.meta && 'default' in fieldBlueprint.meta
        ? fieldBlueprint.meta.default
        : ''
    case 'list':
      const data: any[] = []
      const min = fieldBlueprint.meta.minimumElements || 0
      if (min > 0) {
        for (let i = 0; i < min; i++) {
          data.push(initializeField(fieldBlueprint.meta.of))
        }
      }

      return data
    case 'file':
      return null
    case 'select':
      if (fieldBlueprint.meta.multiple) {
        return 'default' in fieldBlueprint.meta
          ? fieldBlueprint.meta.default
          : []
      } else {
        // TODO/NOTE: Maybe we should return null here. What do we want to use
        // to represent no selection? Empty string or null?
        return 'default' in fieldBlueprint.meta
          ? fieldBlueprint.meta.default
          : ''
      }
    case 'boolean':
      return fieldBlueprint.meta && 'default' in fieldBlueprint.meta
        ? fieldBlueprint.meta.default
        : false
    case 'integer':
      return fieldBlueprint.meta && 'default' in fieldBlueprint.meta
        ? fieldBlueprint.meta.default
        : null
    case 'fieldset':
      const values: IStringAnyMap = {}
      for (const field of fieldBlueprint.fields) {
        values[field.name] = initializeField(field)
      }

      return values
  }

  return N(fieldBlueprint)
}

function initializeForm(formBlueprint: FormBlueprint): IStringAnyMap {
  const values: IStringAnyMap = {}
  for (const field of formBlueprint.fields) {
    values[field.name] = initializeField(field)
  }

  return values
}

export function initialize(blueprint: FormBlueprint | FormFieldBlueprint) {
  if (isFormBlueprint(blueprint)) {
    return initializeForm(blueprint)
  } else {
    return initializeField(blueprint)
  }
}

export function isFieldEmpty(
  fieldBlueprint: FormFieldBlueprint,
  value: any,
): boolean {
  const nullValue = initializeField(fieldBlueprint)

  return LodashFP.isEqual(nullValue, value)
}

export const getFieldValidation = (
  ipath: KeyPath,
  validation: IFormValidation,
): FieldValidation => {
  let fieldValidation: FieldValidation = []
  if (LodashFP.isString(ipath) && ipath in validation.fields) {
    fieldValidation.push.apply(fieldValidation, validation.fields[ipath])
  } else {
    const normalizedPath = stringPath(ipath)
    if (normalizedPath in validation.fields) {
      fieldValidation.push.apply(
        fieldValidation,
        validation.fields[normalizedPath],
      )
    }
  }

  if (fieldValidation.length === 0) {
    fieldValidation.push({
      status: 'ok',
    })
  }

  for (const msg of fieldValidation) {
    if (msg.status === 'ok') {
      // Check if maybe there are nested errors or warnings
      const nested = filterKeys(validation.fields, (key) => {
        return key.startsWith(stringPath(ipath))
      }) as IStringTMap<FieldValidation>

      const count = countWarningsAndErrors({
        validUntil: 'end',
        fields: nested,
      })

      if (count.errors > 0) {
        fieldValidation.push({
          status: 'error',
          message: '',
        })
      } else if (count.warnings > 0) {
        fieldValidation.push({
          status: 'warning',
          message: '',
        })
      }
    }
  }

  if (
    fieldValidation.filter(
      (msg) => msg.status === 'error' || msg.status === 'warning',
    ).length > 0
  ) {
    return fieldValidation.filter((msg) => msg.status !== 'ok')
  } else {
    return fieldValidation
  }
}

export const getFieldGuidelines = (
  ipath: KeyPath,
  guidelines: FormGuidelinesType,
): FieldGuidelinesType => {
  if (LodashFP.isString(ipath) && ipath in guidelines.fields) {
    return guidelines[ipath]
  }

  const normalizedPath = stringPath(ipath)
  if (normalizedPath in guidelines) {
    return guidelines[normalizedPath]
  }

  const normalizedPathWithoutArrayIndexes = arrayPathFlobulator(
    normalizedPath,
  ).replace('[x]', '')
  if (normalizedPathWithoutArrayIndexes in guidelines) {
    return guidelines[normalizedPathWithoutArrayIndexes]
  }

  return emptyFieldGuidelines()
}

export const updateBlueprintOptions = (
  options: OptionMap,
  blueprint: FormBlueprint,
) => {
  const updateField = (
    field: FormFieldBlueprint,
    prefix = '',
  ): FormFieldBlueprint => {
    const fieldName = prefix === '' ? field.name : `${prefix}.${field.name}`

    if (field.type === 'select' && fieldName in options) {
      return {
        ...field,
        meta: {
          ...field.meta,
          options: field.meta.options.map((opt) => {
            const newOptions = options[fieldName]
            const present =
              newOptions.filter((o) => o.value === opt.value).length > 0

            return present
              ? {
                  ...opt,
                  disabled: false,
                }
              : {
                  ...opt,
                  disabled: true,
                }
          }),
        },
      }
    }

    if (field.type === 'fieldset') {
      return {
        ...field,
        fields: field.fields.map((nestedField) => {
          return updateField(nestedField, fieldName)
        }),
      }
    }

    return field
  }

  const newFields = blueprint.fields.map((field) => {
    return updateField(field, '')
  })

  return { ...blueprint, fields: newFields }
}

export const countWarningsAndErrors = (validation: IFormValidation) => {
  let warnings = 0
  let errors = 0

  for (const key of Object.keys(validation.fields)) {
    for (const msg of validation.fields[key]) {
      if (msg.status === 'warning') {
        warnings = warnings + 1
      }

      if (msg.status === 'error') {
        errors = errors + 1
      }
    }
  }

  return { warnings, errors }
}

const applyPrefix = (str: string, prefix = '') => {
  const pp = prefix === '' ? '' : `${prefix}.`

  return `${pp}${str}`
}

export const enumerateBlueprintPaths = (
  blueprint: FormBlueprint,
  prefix: string = '',
): string[] => {
  const mapped = blueprint.fields.map((field) => {
    if (field.type === 'list') {
      const ourPrefix = applyPrefix(`${field.name}[x]`, prefix)
      if (field.meta.of.type === 'fieldset') {
        return [
          applyPrefix(field.name, prefix),
          ourPrefix,
          ...enumerateBlueprintPaths(field.meta.of, ourPrefix),
        ]
      }

      if (field.meta.of.type === 'list') {
        if (field.meta.of.meta.of.type === 'fieldset') {
          return [
            ourPrefix,
            applyPrefix(field.name, prefix),
            ...enumerateBlueprintPaths(
              field.meta.of.meta.of,
              `${ourPrefix}[x]`,
            ),
          ]
        }

        return [applyPrefix(field.name, prefix), ourPrefix, `${ourPrefix}[x]`]
      }

      if (field.meta.of.type === 'text') {
        return applyPrefix(field.name, prefix)
      }

      return [applyPrefix(field.name, prefix), ourPrefix]
    }

    if (field.type === 'fieldset') {
      const ourPrefix = applyPrefix(field.name, prefix)
      return [ourPrefix, ...enumerateBlueprintPaths(field, ourPrefix)]
    }

    return applyPrefix(field.name, prefix)
  })

  return LodashFP.uniq(LodashFP.flattenDeep(mapped)).filter((x) => x != null)
}

export const getTopLevelIndex = (
  formBlueprint: FormBlueprint,
  fieldBlueprint: FormFieldBlueprint,
) => {
  const { fields } = formBlueprint

  for (let index = 0; index < fields.length; index++) {
    const fb = fields[index]

    if (LodashFP.isEqual(fb, fieldBlueprint)) {
      return index
    }

    if (fb.type === 'fieldset') {
      const i = getTopLevelIndex(
        { name: '', fields: fb.fields },
        fieldBlueprint,
      )

      if (i !== -1) {
        return index
      }
    }

    if (fb.type === 'list') {
      if (fb.meta.of.type === 'fieldset') {
        const i = getTopLevelIndex(
          { name: '', fields: fb.meta.of.fields },
          fieldBlueprint,
        )

        if (i !== -1) {
          return index
        }
      }

      const i = getTopLevelIndex(
        { name: '', fields: [fb.meta.of] },
        fieldBlueprint,
      )

      if (i !== -1) {
        return index
      }
    }
  }

  return -1
}

const getNewPath = (ipath: KeyPath) => {
  const elems = splitPath(ipath)
  return elems.splice(1).join('.')
}

export const getBlueprintAtPath = (
  blueprint: FormBlueprint | FormFieldList,
  ipath: KeyPath,
): FormFieldBlueprint | null => {
  const path = arrayPathFlobulator(stringPath(ipath))
  const elems = splitPath(path)

  if (!isFormOrNestedFormBlueprint(blueprint)) {
    if (elems.length === 1 && elems[0] === '[x]') {
      return blueprint
    }

    if (blueprint.meta.of.type === 'fieldset' && elems.length > 1) {
      return getBlueprintAtPath(blueprint.meta.of, getNewPath(elems))
    }

    if (blueprint.meta.of.type === 'list' && elems.length > 1) {
      return getBlueprintAtPath(blueprint.meta.of, getNewPath(elems))
    }

    return null
  }

  const found = blueprint.fields.find((field) => field.name === path)
  if (found) {
    return found
  }

  const prefix = elems[0]
  const foundFirstLevel = blueprint.fields.find(
    (field) => field.name === prefix,
  )

  if (foundFirstLevel && foundFirstLevel.type === 'fieldset') {
    const newPath = getNewPath(path)
    return getBlueprintAtPath(foundFirstLevel, newPath)
  }

  if (foundFirstLevel && foundFirstLevel.type === 'list') {
    if (elems.length > 1 && elems[1] === '[x]') {
      return getBlueprintAtPath(foundFirstLevel, getNewPath(elems))
    }
  }

  return null
}

export const walkBlueprint = (
  blueprint: FormBlueprint,
  cb: (blueprint: FormFieldBlueprint, path: string) => any,
) => {
  const paths = enumerateBlueprintPaths(blueprint)
  for (const path of paths) {
    const pathNode = getBlueprintAtPath(blueprint, path)
    if (pathNode != null) {
      cb(pathNode, path)
    }
  }
}

export const filterBlueprintPaths = (
  blueprint: FormBlueprint,
  cb: (bp: FormFieldBlueprint, path: string) => boolean,
) => {
  const allPaths = enumerateBlueprintPaths(blueprint)
  const paths = []
  for (const path of allPaths) {
    const pathNode = getBlueprintAtPath(blueprint, path)
    if (pathNode != null) {
      const shouldKeep = cb(pathNode, path)
      if (shouldKeep) {
        paths.push(path)
      }
    }
  }

  return paths
}

export const splitPath = (ipath: KeyPath) => {
  const path = stringPath(ipath)
  const elems = path.split('.')

  return LodashFP.flattenDeep(
    elems.map((elem) => {
      if (
        elem.includes('[') &&
        elem.includes(']') &&
        elem.indexOf('[') < elem.indexOf(']')
      ) {
        const result = [] as string[]
        let buffer = ''

        elem.split('').forEach((letter) => {
          switch (letter) {
            case '[':
              result.push(buffer)
              buffer = '['
              break
            case ']':
              result.push(buffer + ']')
              buffer = ''
              break
            default:
              buffer += letter
          }
        })

        return result.filter((x) => x !== '')
      }

      return elem
    }),
  ) as string[]
}

export function joinPath(path: string[]) {
  let joined = ''
  for (let i = 0; i < path.length; i++) {
    if ((path[i].includes('[') && path[i].includes(']')) || i === 0) {
      joined += path[i]
    } else {
      joined += '.' + path[i]
    }
  }
  return joined
}

export function areConditionsReverses(
  cond1: GuidelineCondition,
  cond2: GuidelineCondition,
): boolean {
  const withoutComplement = (cond: GuidelineCondition) =>
    LodashFP.omit(['complement'], cond)

  return (
    LodashFP.isEqual(withoutComplement(cond1), withoutComplement(cond2)) &&
    cond1.complement !== cond2.complement
  )
}

export function areConditionSetsReverses(
  conds1: GuidelineCondition[],
  conds2: GuidelineCondition[],
) {
  if (conds1.length !== conds2.length) {
    return false
  }

  const reversesInOther = conds1.map((cond1) => {
    const numberOfReverses = conds2.filter((cond2) =>
      areConditionsReverses(cond1, cond2),
    )
    return numberOfReverses.length
  })

  return reversesInOther.filter((num) => num === 0).length === 0
}

export function removeUselessConditionalGuidelines(
  guidelines: Guidelines,
  formBlueprint: FormBlueprint,
) {
  const withoutRedundant = {
    ...guidelines,
    conditional: guidelines.conditional.filter((cond) => {
      const params = Object.keys(cond.guidelines)
      const unconditionalParams = LodashFP.pick(
        params,
        guidelines.unconditional,
      )

      let isValid = true
      cond.conditions.forEach((c) => {
        if (c.type === 'empty' && !c.complement) {
          // Condition is for empty field. If field is required then condition
          // is useless.

          const path = removeFirstElementFromPathString(c.field)
          const fieldBlueprint = getBlueprintAtPath(formBlueprint, path)
          if (fieldBlueprint && fieldBlueprint.mandatory) {
            isValid = false
          }
        }
      })

      return isValid && !LodashFP.isEqual(unconditionalParams, cond.guidelines)
    }),
  }

  return withoutRedundant
}

export function removeUnusedValuesFromObject(value: string, object: object) {
  return LodashFP.omit(value, object)
}
